import { getContract, erc20ABI } from '@wagmi/core';
import { BigNumber, ethers, Signer, Contract } from 'ethers';
import BN from 'bignumber.js';

import { zeroContract } from 'config/config-env';

import { 
  TokenT,
  OrderType,
  OrderSide,
} from 'types';

type signCreateOrderProps = {
  order: {
    baseToken: TokenT;
    quoteToken: TokenT;
    limitPrice: string;
    amount: string;
  };
  orderType: string;
  tradeSide: string;
  masterContract: Contract;
  signer: Signer;
  gasPrice: BigNumber;
  slippage?: string;
};

export const approveToken = async (signer: Signer, token: TokenT, amount: string, escrowContractAddress: string) => {
  const million = BigNumber.from(10).pow(token.decimals).mul(1000000);

  if (token.address !== zeroContract) {
    const contract = getContract({
      address: token.address,
      abi: erc20ABI,
      signerOrProvider: signer,
    });

    //@ts-ignore
    const allowSize = await contract.allowance(signer._address, escrowContractAddress);
    const allowSizeFormated = ethers.utils.formatUnits(allowSize.toString(), token.decimals);

    if (Number(allowSizeFormated) < Number(amount)) {
      //@ts-ignore
      const allowResponse = await contract.approve(escrowContractAddress, million);
      await allowResponse.wait();
    }
  }
};

const handleMarketBuyOrder = async (
  masterContract: Contract,
  baseCurrency: TokenT,
  quoteCurrency: TokenT,
  amount: string,
  gasPrice: BigNumber,
  slippage: string
) => {
  const isNativeToken = quoteCurrency.address === zeroContract;
  const tokensAmount = ethers.utils.parseUnits(amount, quoteCurrency.decimals);

  const signResult = await masterContract.createMarketBuyOrder(
    tokensAmount,
    baseCurrency.address,
    quoteCurrency.address,
    ethers.utils.parseUnits(slippage, 2),
    {
      value: isNativeToken ? tokensAmount.add(gasPrice) : gasPrice,
    }
  );
  const tx = await signResult?.wait();

  return tx;
};

const handleMarketSellOrder = async (
  connectedMasterContract: Contract,
  baseCurrency: TokenT,
  quoteCurrency: TokenT,
  amount: string,
  gasPrice: BigNumber,
  slippage: string
) => {
  const isNativeToken = baseCurrency.address === zeroContract;
  const tokensAmount = ethers.utils.parseUnits(amount, baseCurrency.decimals);

  const signResult = await connectedMasterContract.createMarketSellOrder(
    tokensAmount,
    baseCurrency.address,
    quoteCurrency.address,
    ethers.utils.parseUnits(slippage, 2),
    {
      value: isNativeToken ? tokensAmount.add(gasPrice) : gasPrice,
    }
  );
  const tx = await signResult?.wait();

  return tx;
};

const handleLimitBuyOrder = async (
  connectedMasterContract: Contract,
  baseCurrency: TokenT,
  quoteCurrency: TokenT,
  amount: string,
  gasPrice: BigNumber,
  limitPrice: string
) => {
  const isNativeToken = quoteCurrency.address === zeroContract;
  const properAmount = BN(amount).toFixed(quoteCurrency.decimals);
  let tokensAmount = BN(properAmount).div(BN(limitPrice)).toFixed(baseCurrency.decimals);
  let smAmount = BN(tokensAmount).times(limitPrice).toFixed(quoteCurrency.decimals, BN.ROUND_DOWN);

  if (smAmount != properAmount) {
    tokensAmount = BN(properAmount).div(BN(limitPrice)).toFixed(baseCurrency.decimals, BN.ROUND_UP);
  }

  const bnTokensAmount = ethers.utils.parseUnits(tokensAmount, baseCurrency.decimals);

  const signResult = await connectedMasterContract.createBuyOrder(
    bnTokensAmount,
    baseCurrency.address,
    quoteCurrency.address,
    ethers.utils.parseUnits(limitPrice.toString(), quoteCurrency.decimals),
    {
      value: isNativeToken ? bnTokensAmount.add(gasPrice) : gasPrice,
    }
  );
  const tx = await signResult?.wait();

  return tx;
};

const handleLimitSellOrder = async (
  connectedMasterContract: Contract,
  baseCurrency: TokenT,
  quoteCurrency: TokenT,
  amount: string,
  gasPrice: BigNumber,
  limitPrice: string
) => {
  const isNativeToken = baseCurrency.address === zeroContract;
  const tokensAmount = ethers.utils.parseUnits(amount, baseCurrency.decimals);

  const signResult = await connectedMasterContract.createSellOrder(
    tokensAmount,
    baseCurrency.address,
    quoteCurrency.address,
    ethers.utils.parseUnits(limitPrice.toString(), quoteCurrency.decimals),
    {
      value: isNativeToken ? tokensAmount.add(gasPrice) : gasPrice,
    }
  );
  const tx = await signResult?.wait();

  return tx;
};

export const signCreateOrder = async ({
  order,
  orderType,
  tradeSide,
  masterContract,
  signer,
  gasPrice,
  slippage = '0',
}: signCreateOrderProps) => {
  const { baseToken, quoteToken, limitPrice, amount } = order;

  if (masterContract && signer) {
    const connectedMasterContract = masterContract.connect(signer);
    let tx;

    if (orderType === OrderType.Market) {
      // handle Market order
      if (tradeSide === OrderSide.Buy) {
        tx = await handleMarketBuyOrder(connectedMasterContract, baseToken, quoteToken, amount, gasPrice, slippage);
      } else {
        tx = await handleMarketSellOrder(connectedMasterContract, baseToken, quoteToken, amount, gasPrice, slippage);
      }
    } else {
      // handle Limit order
      if (tradeSide === OrderSide.Buy) {
        tx = await handleLimitBuyOrder(connectedMasterContract, baseToken, quoteToken, amount, gasPrice, limitPrice);
      } else {
        tx = await handleLimitSellOrder(connectedMasterContract, baseToken, quoteToken, amount, gasPrice, limitPrice);
      }
    }

    return tx?.transactionHash;
  }
};

export const signCancelOrder = async (escrowContract: Contract, id: number) => {
  const signResult = await escrowContract.cancelOrder(id);
  const tx = await signResult?.wait();

  return tx;
};
